import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Megaphone, ArrowUp, ArrowDown } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { title: "", description: "", image: "", button_label: "", button_link: "", active: true, order: 0 };

export default function Marketing() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/campaigns", { params: { all: true } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: items.length }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setOpen(true); };

  const save = async () => {
    const payload = { ...form, order: Number(form.order) };
    try {
      if (editing) await api.put(`/campaigns/${editing.id}`, payload);
      else await api.post("/campaigns", payload);
      toast.success("Campanha salva"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/campaigns/${id}`); toast.success("Removida"); load(); } catch { toast.error("Erro"); } };
  const move = async (c, dir) => { try { await api.put(`/campaigns/${c.id}`, { ...c, order: c.order + dir }); load(); } catch {} };
  const toggle = async (c) => { try { await api.put(`/campaigns/${c.id}`, { ...c, active: !c.active }); load(); } catch {} };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Marketing</h1><p className="text-zinc-400 mt-1">Campanhas exibidas no carrossel da página inicial.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={openNew} data-testid="add-campaign-btn"><Plus className="w-4 h-4 mr-1" /> Nova</Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-500"><Megaphone className="w-10 h-10 mx-auto mb-3 text-zinc-600" />Nenhuma campanha. Crie a primeira!</div>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex flex-col sm:flex-row gap-4" data-testid={`campaign-item-${c.id}`}>
              {c.image && <img src={c.image} alt={c.title} className="w-full sm:w-40 h-28 object-cover rounded-lg" />}
              <div className="flex-1">
                <div className="flex items-center gap-2"><h3 className="font-heading font-bold text-white">{c.title}</h3>{!c.active && <span className="text-xs text-red-400">Inativa</span>}</div>
                <p className="text-sm text-zinc-400 mt-1">{c.description}</p>
                {c.button_label && <p className="text-xs text-amber-400 mt-2">Botão: {c.button_label} → {c.button_link}</p>}
              </div>
              <div className="flex sm:flex-col gap-2 items-center">
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" className="border-zinc-700 text-zinc-400 h-8 w-8" onClick={() => move(c, -1)} data-testid={`campaign-up-${c.id}`}><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="icon" variant="outline" className="border-zinc-700 text-zinc-400 h-8 w-8" onClick={() => move(c, 1)} data-testid={`campaign-down-${c.id}`}><ArrowDown className="w-4 h-4" /></Button>
                </div>
                <div className="flex gap-1">
                  <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`campaign-toggle-${c.id}`} />
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" className="border-zinc-700 text-zinc-300 h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-campaign-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="outline" className="border-red-500/40 text-red-400 h-8 w-8" onClick={() => remove(c.id)} data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Editar" : "Nova"} Campanha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="campaign-title" /></div>
            <div><Label className="text-zinc-300">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="campaign-description" /></div>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} aspect={16 / 9} label="Imagem/Arte da campanha" testid="campaign-image" previewClass="h-40" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-300">Texto do Botão</Label><Input value={form.button_label} onChange={(e) => setForm({ ...form, button_label: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="campaign-btn-label" /></div>
              <div><Label className="text-zinc-300">Link do Botão</Label><Input value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })} placeholder="/agendar" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="campaign-btn-link" /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="campaign-active" /><Label className="text-zinc-300">Ativa</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-amber-400 text-black font-bold" data-testid="save-campaign-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
